import {AlertIcon} from '@primer/octicons-react'
import {PageLayout, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import type {FC, ReactNode} from 'react'

type FindingsVariant = 'page' | 'overview'
type HeadingTag = 'h2' | 'h3'

interface FindingsLifecycleContainerProps {
  variant: FindingsVariant
  children: ReactNode
}

interface FindingsErrorProps {
  variant: FindingsVariant
  headingTag: HeadingTag
  error?: string | null
}

export const FindingsError: FC<FindingsErrorProps> = ({variant, error, headingTag}) => (
  <FindingsLifecycleContainer variant={variant}>
    <Blankslate className="border rounded-3" role="alert">
      <Blankslate.Visual>
        <AlertIcon size="medium" className="color-fg-danger" />
      </Blankslate.Visual>
      <Blankslate.Heading as={headingTag}>Failed to load findings</Blankslate.Heading>
      {error && <Blankslate.Description>{error}</Blankslate.Description>}
    </Blankslate>
  </FindingsLifecycleContainer>
)

export const FindingsLoadingState: FC<{variant: FindingsVariant}> = ({variant}) => (
  <FindingsLifecycleContainer variant={variant}>
    <div className="d-flex flex-justify-center tmp-p-6" role="status" aria-label="Loading findings">
      <Spinner size="large" delay="short" />
    </div>
  </FindingsLifecycleContainer>
)

export const FindingsNotFoundState: FC<{variant: FindingsVariant; headingTag: HeadingTag}> = ({
  variant,
  headingTag,
}) => (
  <FindingsLifecycleContainer variant={variant}>
    <Blankslate className="border rounded-3">
      <Blankslate.Heading as={headingTag}>No findings available</Blankslate.Heading>
      <Blankslate.Description>
        Copilot has not detected any findings for this pull request. This could be because the analysis has not
        completed yet, or because there are no findings to report.
      </Blankslate.Description>
    </Blankslate>
  </FindingsLifecycleContainer>
)

export const FindingsProcessingState: FC<{variant: FindingsVariant; headingTag: HeadingTag}> = ({
  variant,
  headingTag,
}) => (
  <FindingsLifecycleContainer variant={variant}>
    <Blankslate className="border rounded-3">
      <Blankslate.Heading as={headingTag}>Processing findings</Blankslate.Heading>
      <Blankslate.Description>Findings are being processed. This page will update automatically</Blankslate.Description>
    </Blankslate>
  </FindingsLifecycleContainer>
)

export const FindingsNoFindingsState: FC<{variant: FindingsVariant; headingTag: HeadingTag}> = ({
  variant,
  headingTag,
}) => (
  <FindingsLifecycleContainer variant={variant}>
    <Blankslate className="border rounded-3">
      <Blankslate.Heading as={headingTag}>No findings</Blankslate.Heading>
      <Blankslate.Description>There are no findings for this pull request</Blankslate.Description>
    </Blankslate>
  </FindingsLifecycleContainer>
)

const FindingsLifecycleContainer: FC<FindingsLifecycleContainerProps> = ({variant, children}) => {
  if (variant === 'overview') {
    return <div>{children}</div>
  }

  return (
    <PageLayout.Content as="div" padding="none">
      {children}
    </PageLayout.Content>
  )
}
